import { Button, Box, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { makeBookingInDayViewCalendar } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingComponents'
import { getCPDataWithParams } from 'src/clinic/services'
import HoldSlotAndSendLInkToPayButton from 'src/clinic/components/ClinicManageBookingsPage/newBookingCalendar/HoldSlotAndSendLInkToPayButton'
import {
  allAvailableEventTypes,
  getSessionDisplayLabel,
  PAY_IN_FUTURE_ALLOWED_MENTOR_IDS,
} from 'src/consumer/constants'
import { getDataMapFromCommonResponse, isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'

export default function BookingDialogActions({
  isEnoughTimeToBook,
  askPayment,
  paymentDetail,
  eventDataForSelectedEventType,
  forMentor,
  tryBookingScreening,
  reasonForNotDoingScreening,
  bookNowHandler,
  paymentLinkToSendViaQRCodeFunction,
  clinicId,
  clinicBrandId,
  mentorProfileData,
  clickedWantedSlotData,
  selectedPatient,
  updateBookingsInCalendar,
  setNewBookingDialogOpen,
  setBookingLoading,
  setSelectedPatient,
  setIsNewPatient,
  setNewPatientInputData,
  chosenChamberRoomId,
  selectedRelatedConsultationForTherapy,
  meetingBookApiSignature,
  relatedToPatient,
  forceHoldingSlot = false,
  hideBookButton = false,
}) {
  const [creditsContext, setCreditsContext] = useState(null)
  const [creditsContextLoading, setCreditsContextLoading] = useState(false)
  let showHoldSlotButton = false
  if (forMentor && paymentDetail?.feePaymentMode === 'aumhum') {
    showHoldSlotButton = true
  }
  if (forceHoldingSlot) {
    showHoldSlotButton = true
  }

  const creditsContextApiSignature = useMemo(() => {
    if (!meetingBookApiSignature) {
      return '/api/secure/meeting/booking-credits-context'
    }
    return meetingBookApiSignature.replace(/\/book$/, '/booking-credits-context')
  }, [meetingBookApiSignature])

  const bookButtonSessionLabel = getSessionDisplayLabel(eventDataForSelectedEventType)

  useEffect(() => {
    let active = true

    const loadCreditsContext = async () => {
      if (!selectedPatient?.id || !eventDataForSelectedEventType?.subscriptionPlanId) {
        setCreditsContext(null)
        return
      }

      setCreditsContextLoading(true)
      const response = await getCPDataWithParams(creditsContextApiSignature, {
        patientId: selectedPatient.id,
        subscriptionPlanId: eventDataForSelectedEventType.subscriptionPlanId,
        eventTypeId: eventDataForSelectedEventType?.uid || eventDataForSelectedEventType?.id,
        eventType: eventDataForSelectedEventType?.type,
        mentorId: mentorProfileData?.id,
        clinicId,
        clinicBrandId,
      })

      if (!active) {
        return
      }

      if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
        setCreditsContext(getDataMapFromCommonResponse(response)?.creditsContext || null)
      } else {
        setCreditsContext(null)
      }
      setCreditsContextLoading(false)
    }

    loadCreditsContext()

    return () => {
      active = false
    }
  }, [
    clinicBrandId,
    clinicId,
    creditsContextApiSignature,
    eventDataForSelectedEventType?.id,
    eventDataForSelectedEventType?.subscriptionPlanId,
    eventDataForSelectedEventType?.type,
    eventDataForSelectedEventType?.uid,
    mentorProfileData?.id,
    selectedPatient?.id,
  ])

  const canUseCredits = creditsContext?.eligible === true

  return (
    <div className="d-flex flex-column w-100">
      <Box
        sx={{
          display: 'flex',
          gap: '12px',
          flexDirection: 'column',
          width: '100%',
          '@media (min-width: 600px)': {
            flexDirection: 'row-reverse',
          },
        }}
      >
        {canUseCredits && (
          <Button
            sx={{
              width: '100%',
              minHeight: '48px',
              fontSize: '16px',
              fontWeight: 600,
              marginBottom: '8px',
              textTransform: 'none',
              borderWidth: '1.5px',
              borderColor: 'rgba(16, 185, 129, 0.4)',
              color: 'rgba(4, 120, 87, 0.95)',
              borderRadius: '10px',
              backgroundColor: 'rgba(16, 185, 129, 0.06)',
              letterSpacing: '0.2px',
              '&:hover': {
                borderColor: 'rgba(16, 185, 129, 0.6)',
                backgroundColor: 'rgba(16, 185, 129, 0.12)',
              },
              '&:disabled': {
                borderColor: 'rgba(16, 185, 129, 0.2)',
                color: 'rgba(4, 120, 87, 0.5)',
              },
              '@media (min-width: 600px)': {
                width: 'auto',
                minWidth: '220px',
              },
            }}
            variant="outlined"
            disabled={!isEnoughTimeToBook || creditsContextLoading}
            onClick={() =>
              makeBookingInDayViewCalendar(
                forMentor,
                clinicId,
                clinicBrandId,
                mentorProfileData?.id,
                false,
                clickedWantedSlotData,
                eventDataForSelectedEventType,
                selectedPatient,
                updateBookingsInCalendar,
                setNewBookingDialogOpen,
                paymentDetail,
                setBookingLoading,
                setSelectedPatient,
                setIsNewPatient,
                setNewPatientInputData,
                chosenChamberRoomId,
                tryBookingScreening,
                false,
                null,
                'credits',
                selectedRelatedConsultationForTherapy,
                meetingBookApiSignature,
                false,
                false,
                false,
                [],
                creditsContext?.recommendedBundlePackPurchaseId || '',
              )
            }
          >
            {creditsContextLoading ? 'Checking credits...' : 'Use Pack / Credits'}
          </Button>
        )}
        {!hideBookButton && (
          <Button
            sx={{
              width: '100%',
              minHeight: '48px',
              fontSize: '16px',
              fontWeight: 700,
              marginBottom: '8px',
              textTransform: 'none',
              background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.95) 0%, rgba(99, 102, 241, 0.95) 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '10px',
              boxShadow: '0 4px 14px rgba(67, 56, 202, 0.4)',
              letterSpacing: '0.3px',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(67, 56, 202, 1) 0%, rgba(99, 102, 241, 1) 100%)',
                boxShadow: '0 6px 20px rgba(67, 56, 202, 0.5)',
                transform: 'translateY(-2px)',
              },
              '&:active': {
                transform: 'translateY(0)',
                boxShadow: '0 2px 8px rgba(67, 56, 202, 0.4)',
              },
              '&:disabled': {
                background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.4) 0%, rgba(99, 102, 241, 0.4) 100%)',
                color: 'rgba(255, 255, 255, 0.6)',
                boxShadow: 'none',
                cursor: 'not-allowed',
              },
              '@media (min-width: 600px)': {
                width: 'auto',
                minWidth: '200px',
              },
            }}
            variant="contained"
            disabled={(() => {
              if (!isEnoughTimeToBook) {
                return true
              }
              let toReturn = false
              if (askPayment) {
                if (paymentDetail?.feePaid && paymentDetail?.feePaymentMode) {
                  if (paymentDetail?.feePaymentMode === 'cash') {
                    toReturn = false
                  } else if (paymentDetail?.feePaymentMode !== 'cash') {
                    toReturn = false
                  } else {
                    toReturn = true
                  }
                } else {
                  toReturn = true
                }
              } else {
                toReturn = false
              }
              if (
                eventDataForSelectedEventType?.type === allAvailableEventTypes.consultation &&
                !forMentor
              ) {
                if (!tryBookingScreening) {
                  if (!reasonForNotDoingScreening) {
                    toReturn = true
                  }
                }
              }

              return toReturn
            })()}
            onClick={bookNowHandler}
          >
            Book {bookButtonSessionLabel}{' '}
            {eventDataForSelectedEventType?.meetingSrc === 'offline' && '(In Clinic)'}
            {eventDataForSelectedEventType?.meetingSrc === 'aumhum' && '(Online/Video)'}
          </Button>
        )}
        {forMentor &&
          paymentLinkToSendViaQRCodeFunction &&
          paymentDetail?.feePaymentMode === 'aumhum' && (
            <Button
              sx={{
                width: '100%',
                minHeight: '48px',
                fontSize: '16px',
                fontWeight: 600,
                marginBottom: '8px',
                textTransform: 'none',
                borderWidth: '1.5px',
                borderColor: 'rgba(67, 56, 202, 0.4)',
                color: 'rgba(67, 56, 202, 0.9)',
                borderRadius: '10px',
                letterSpacing: '0.3px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'rgba(67, 56, 202, 0.6)',
                  backgroundColor: 'rgba(67, 56, 202, 0.08)',
                  color: 'rgba(67, 56, 202, 1)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(67, 56, 202, 0.15)',
                },
                '&:active': {
                  backgroundColor: 'rgba(67, 56, 202, 0.12)',
                  transform: 'translateY(0)',
                },
                '&:disabled': {
                  borderColor: 'rgba(67, 56, 202, 0.2)',
                  color: 'rgba(67, 56, 202, 0.4)',
                  backgroundColor: 'transparent',
                  cursor: 'not-allowed',
                },
                '@media (min-width: 600px)': {
                  width: 'auto',
                  minWidth: '200px',
                },
              }}
              variant="outlined"
              disabled={(() => {
                if (!isEnoughTimeToBook) {
                  return true
                }
                if (askPayment) {
                  if (paymentDetail?.feePaid && paymentDetail?.feePaymentMode) {
                    if (paymentDetail?.feePaymentMode === 'cash') {
                      return false
                    } else if (paymentDetail?.feePaymentMode !== 'cash') {
                      return false
                    } else {
                      return true
                    }
                  } else {
                    return true
                  }
                } else {
                  return false
                }
              })()}
              onClick={async () => {
                const returnedObject = await makeBookingInDayViewCalendar(
                  forMentor,
                  clinicId,
                  clinicBrandId,
                  mentorProfileData?.id,
                  askPayment,
                  clickedWantedSlotData,
                  eventDataForSelectedEventType,
                  selectedPatient,
                  updateBookingsInCalendar,
                  setNewBookingDialogOpen,
                  {
                    feePaid: paymentDetail?.feePaid,
                    feePaymentMode: paymentDetail?.feePaymentMode,
                    feePaidTxnId: paymentDetail?.feePaidTxnId,
                    currency: paymentDetail?.currency,
                    feeCollectionNote: paymentDetail?.feeCollectionNote || '',
                  },
                  setBookingLoading,
                  setSelectedPatient,
                  setIsNewPatient,
                  setNewPatientInputData,
                  chosenChamberRoomId,
                  tryBookingScreening,
                  true,
                  null,
                  null,
                  selectedRelatedConsultationForTherapy,
                  meetingBookApiSignature,
                  true,
                )
                if (paymentLinkToSendViaQRCodeFunction) {
                  paymentLinkToSendViaQRCodeFunction({
                    ...returnedObject,
                    currentPatient: selectedPatient,
                  })
                }
              }}
            >
              Hold Slot & Show Payment QR to Patient
            </Button>
          )}
        {showHoldSlotButton && (
          <HoldSlotAndSendLInkToPayButton
            forceDisable={!isEnoughTimeToBook}
            askPayment={askPayment}
            paymentDetail={paymentDetail}
            forMentor={forMentor}
            clinicId={clinicId}
            clinicBrandId={clinicBrandId}
            mentorProfileData={mentorProfileData}
            clickedWantedSlotData={clickedWantedSlotData}
            eventDataForSelectedEventType={eventDataForSelectedEventType}
            selectedPatient={selectedPatient}
            updateBookingsInCalendar={updateBookingsInCalendar}
            setNewBookingDialogOpen={setNewBookingDialogOpen}
            setBookingLoading={setBookingLoading}
            setSelectedPatient={setSelectedPatient}
            setIsNewPatient={setIsNewPatient}
            setNewPatientInputData={setNewPatientInputData}
            chosenChamberRoomId={chosenChamberRoomId}
            tryBookingScreening={tryBookingScreening}
            selectedRelatedConsultationForTherapy={selectedRelatedConsultationForTherapy}
            meetingBookApiSignature={meetingBookApiSignature}
            relativeInformation={
              selectedPatient?.primaryPatientProfile
                ? {
                    ...relatedToPatient,
                    dependentRelation: selectedPatient?.primaryPatientProfile?.dependentRelation,
                  }
                : null
            }
          />
        )}
        {forMentor && PAY_IN_FUTURE_ALLOWED_MENTOR_IDS.includes(mentorProfileData?.id) && (
          <Button
            sx={{
              width: '100%',
              minHeight: '48px',
              fontSize: '16px',
              fontWeight: 600,
              marginBottom: '8px',
              textTransform: 'none',
              borderWidth: '1.5px',
              borderColor: 'rgba(67, 56, 202, 0.4)',
              color: 'rgba(67, 56, 202, 0.9)',
              borderRadius: '10px',
              letterSpacing: '0.3px',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'rgba(67, 56, 202, 0.6)',
                backgroundColor: 'rgba(67, 56, 202, 0.08)',
                color: 'rgba(67, 56, 202, 1)',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(67, 56, 202, 0.15)',
              },
              '&:active': {
                backgroundColor: 'rgba(67, 56, 202, 0.12)',
                transform: 'translateY(0)',
              },
              '&:disabled': {
                borderColor: 'rgba(67, 56, 202, 0.2)',
                color: 'rgba(67, 56, 202, 0.4)',
                backgroundColor: 'transparent',
                cursor: 'not-allowed',
              },
              '@media (min-width: 600px)': {
                width: 'auto',
                minWidth: '200px',
              },
            }}
            variant="outlined"
            disabled={(() => {
              if (!isEnoughTimeToBook) {
                return true
              }
              // Match the main booking button's disabled logic for consistency
              if (askPayment) {
                if (paymentDetail?.feePaid && paymentDetail?.feePaymentMode) {
                  if (paymentDetail?.feePaymentMode === 'cash') {
                    return false
                  } else if (paymentDetail?.feePaymentMode !== 'cash') {
                    return false
                  } else {
                    return true
                  }
                } else {
                  return true
                }
              } else {
                return false
              }
            })()}
            onClick={() => {
              makeBookingInDayViewCalendar(
                forMentor,
                clinicId,
                clinicBrandId,
                mentorProfileData?.id,
                askPayment,
                clickedWantedSlotData,
                eventDataForSelectedEventType,
                selectedPatient,
                updateBookingsInCalendar,
                setNewBookingDialogOpen,
                {
                  feePaid: paymentDetail?.feePaid,
                  feePaymentMode: paymentDetail?.feePaymentMode,
                  feePaidTxnId: paymentDetail?.feePaidTxnId,
                  currency: paymentDetail?.currency,
                  feeCollectionNote: paymentDetail?.feeCollectionNote || '',
                },
                setBookingLoading,
                setSelectedPatient,
                setIsNewPatient,
                setNewPatientInputData,
                chosenChamberRoomId,
                tryBookingScreening,
                false,
                null,
                'payInFuture',
                selectedRelatedConsultationForTherapy,
              )
            }}
          >
            Auto Deduct Credits in Future (Beta)
          </Button>
        )}
      </Box>
      {canUseCredits && creditsContext?.summary ? (
        <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, mb: 1 }}>
          {creditsContext.summary}
        </Typography>
      ) : null}
      {!isEnoughTimeToBook && (
        <div
          style={{ fontWeight: 500, textAlign: 'center', color: '#b73c30', marginBottom: '20px' }}
        >
          This session overlaps with other sessions! Please select a different event type or time to
          book.
        </div>
      )}
    </div>
  )
}
